import {
  Box,
  Button,
  CardContent,
  Collapse,
  Divider,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { DiscoverViewContext } from "contexts/DiscoverView";
import { QuickSendDrawerContext } from "contexts/QuickSendDrawer";
import { forwardRef, useContext, useEffect, useState } from "react";
import { BentoBrand } from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";

import { trackEvent } from "utils/tracking";
import { useCollection } from "utils/useCollection";

import BrandBody from "../../DetailedBrandView/components/BrandBody";
import { useBrandDetails } from "../../DetailedBrandView/components/BrandBody/useBrandDetails";
import BrandMetadata from "../../DetailedBrandView/components/BrandMetadata";
import BrandIcons from "../../Shared/BrandIcons";
import BrandSocialLinks from "../../Shared/BrandSocialLinks";
import PitchedIcon from "../../Shared/PitchedIcon";
import BrandCardActions from "../BrandCard/BrandCardActions";
import { BrandCardSource } from "../schema";
import AboutSummary from "./AboutSummary";
import FavoriteIconButton from "./FavoriteIconButton";

interface BrandCardProps {
  brand: BentoBrand;
}

const styles = {
  brandCard: {
    width: "100%",
    borderRadius: 2,
    border: `1px solid ${grey[300]}`,
    backgroundColor: "white",
  },
  brandContent: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    minWidth: 0,
  },
  brandName: {
    fontWeight: 700,
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
  },
  draft: {
    color: "error.main",
  },
  reco: {
    color: "info.main",
  },
  websiteButton: {
    textTransform: "none",
    color: grey[600],
    fontSize: 12,
  },
  divider: {
    borderColor: grey[200],
    my: 2,
  },
};

export default forwardRef<HTMLDivElement, BrandCardProps>(({ brand }, ref) => {
  const { collapseDrawerIfOpened } = useContext(QuickSendDrawerContext);
  const { setBrandNavigationHistory } = useContext(DiscoverViewContext);
  const [expanded, setExpanded] = useState(false);
  const theme = useTheme();

  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("lg"));
  const isMediumLargeScreen = useMediaQuery(
    "(min-width:1200px) and (max-width:1400px)",
  );
  const isLargeScreen = useMediaQuery(
    "(min-width:1400px) and (max-width:1760px)",
  );
  const { discoverTab } = useContext(DiscoverViewContext);

  const isSmallWidth =
    (isMediumScreen && discoverTab !== null) ||
    (isMediumLargeScreen && discoverTab === null) ||
    (isLargeScreen && discoverTab !== null);

  const source = BrandCardSource.DISCOVER_BRAND;
  const { isDraftBrand, isAutogeneratedDraftBrand } = useCollection();

  const isDrafted = isDraftBrand(brand);
  const isAutogeneratedDraft = isAutogeneratedDraftBrand(brand);

  const handleClickCard = () => {
    trackEvent("Brand Card Clicked", {
      "Bento Brand ID": brand?.id,
      source,
    });
    setBrandNavigationHistory([brand]);
    setExpanded(!expanded);
    collapseDrawerIfOpened();
  };

  const {
    fetchSelectedBentobrand,
    selectedBrand,
    fetchLoading,
    similarBrands,
    fetchSimilarLoading,
  } = useBrandDetails({ brandDetailsId: brand?.id });

  useEffect(() => {
    if (brand?.id && expanded) {
      fetchSelectedBentobrand(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand?.id, expanded]);

  return (
    <Box ref={ref} sx={styles.brandCard} key={brand?.id} component="div">
      <Box display="flex" width="100%">
        <Box sx={[styles.brandContent, { minHeight: "275px" }]}>
          <Box
            sx={{
              flex: "1 0 auto",
              py: 2,
              px: 2,
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 2,
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  minWidth: 80,
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  overflow: "hidden",
                  border: `1px solid ${grey[300]}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: grey[100],
                  flexShrink: 0,
                }}
              >
                {brand.logoUrl ? (
                  <Box
                    component="img"
                    src={brand.logoUrl}
                    alt={`${brand.brandName} logo`}
                    onError={(e: any) => {
                      e.target.onerror = null;
                      e.target.style.display = "none";
                    }}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : null}

                {!brand.logoUrl && (
                  <Typography
                    variant="h3"
                    sx={{ color: grey[600], fontWeight: 700 }}
                  >
                    {brand.brandName?.[0]?.toUpperCase() || "?"}
                  </Typography>
                )}
              </Box>

              <Box
                sx={{
                  flex: "1 1 auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  minWidth: 0,
                }}
              >
                <Grid
                  container
                  alignItems="center"
                  justifyContent="space-between"
                  wrap="nowrap"
                >
                  <Grid item xs zeroMinWidth>
                    <Typography variant="h6" sx={styles.brandName}>
                      {brand.brandName}
                      {(isDrafted || isAutogeneratedDraft) && (
                        <>
                          ,{" "}
                          {isAutogeneratedDraft ? (
                            <Box component="span" sx={styles.reco}>
                              Reco{" "}
                              <Box
                                component="i"
                                className="fa-solid fa-wand-magic-sparkles"
                              />
                            </Box>
                          ) : (
                            <Box component="span" sx={styles.draft}>
                              Draft
                            </Box>
                          )}
                        </>
                      )}
                    </Typography>
                  </Grid>

                  <Grid item xs="auto">
                    <FavoriteIconButton brand={brand} />
                  </Grid>
                </Grid>

                <BrandIcons bentoBrand={brand} />

                <BrandSocialLinks
                  instagramFollowerCount={brand.instagramFollowerCount}
                  instagramUsername={brand.instagramUsername}
                  bentoBrandId={brand.id}
                  website={brand.website}
                  source={source}
                />
              </Box>
            </Box>

            <Divider sx={styles.divider} />

            <Box
              display="flex"
              flexDirection="row"
              gap={0.5}
              sx={{
                overflowX: "auto",
                overflowY: "hidden",
                whiteSpace: "nowrap",
                pr: 0.5,
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": {
                  width: 0,
                },
              }}
            >
              <BrandMetadata selectedBrand={brand} noGrid />
            </Box>

            <Box onClick={handleClickCard} sx={{ cursor: "pointer", mt: 1 }}>
              <AboutSummary bentoBrand={brand} expanded={expanded} />
            </Box>

            <Box sx={{ flexShrink: 0, mt: "auto", mr: -1.5 }}>
              <Grid
                container
                alignItems="center"
                justifyContent="space-between"
              >
                {isSmallScreen ? (
                  <Grid item xs="auto">
                    <Button
                      size="small"
                      variant="outlined"
                      color="inherit"
                      sx={styles.websiteButton}
                      component="a"
                      target="_blank"
                      href={`https://${brand?.website}`}
                      onClick={(e: CustomEvent) => {
                        e.stopPropagation();
                        trackEvent("Website Brand Card Clicked", {
                          Website: brand.website,
                          "Bento Brand ID": brand.id,
                        });
                      }}
                    >
                      Website
                      <Box
                        component="span"
                        className="fa-solid fa-up-right-from-square"
                        sx={{ ml: 0.75 }}
                      />
                    </Button>
                  </Grid>
                ) : (
                  <Grid item xs="auto" />
                )}

                <Grid item container alignItems="center" xs="auto">
                  {brand.emailedBrand && (
                    <Grid item xs="auto" sx={{ mr: 0.5 }}>
                      <PitchedIcon size="small" />
                    </Grid>
                  )}
                  <BrandCardActions source={source} brand={brand} />
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Box>
      </Box>

      {expanded && (
        <Collapse in={expanded}>
          <CardContent>
            <BrandBody
              selectedBrand={selectedBrand}
              fetchLoading={fetchLoading}
              similarBrands={similarBrands}
              fetchSimilarLoading={fetchSimilarLoading}
              source={source}
              mobileOverride={isSmallWidth}
            />
          </CardContent>
        </Collapse>
      )}
    </Box>
  );
});
